import {Injectable} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Stage} from '../../../../types/stage';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {LocalStorageService} from '../../../../../api/services/localStorage';
import {HttpHandlingService} from '../../../../../api/services/http-handling.service';
import {Competition, CompetitionType} from '../../../../types/competition';
import {CompetitorSimple} from '../../../../types/competitor';
import {SERVER_API_URL} from '../../../../types/app.constants';
import { Round } from 'src/app/common/types/round';
import { MatDialog } from '@angular/material/dialog';
import { RoundDialogComponent } from '../add-round-dialog/round-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class AddCompetitionCardService {

  competitionCardForm = new UntypedFormGroup({
    competitionName: new UntypedFormControl('', Validators.required),
    registrationEnabledFrom: new UntypedFormControl('', Validators.required),
    registrationEnabledTo: new UntypedFormControl('', Validators.required),
    competitionStart: new UntypedFormControl('', Validators.required),
    competitionEnd: new UntypedFormControl('', Validators.required),
    author: new UntypedFormControl(''),
  });

  rounds: Round[] = [];
  round: Round | null = null;
  stage: Stage = null;
  authors: number[] = [];
  authorsFullData: CompetitorSimple[] = [];
  activeAuthors: CompetitorSimple[] = [];

  competition: Competition = null;

  constructor(private http: HttpClient, private router: Router, private localStorage: LocalStorageService,
              private httpHandlingService: HttpHandlingService, public dialog: MatDialog) {}

  /**
   * function for saving newly created competitions
   */
  onSaveCompetition(): void {
    console.log(this.onReturnCompetition());
    this.http.post<Competition>(SERVER_API_URL + '/api/competitionWithCecTablesList', this.onReturnCompetition(),
      {headers: this.httpHandlingService.authorizationHeaders()}).subscribe((competition: Competition) => {
      this.router.navigate(['/competition', competition.id, 'edit']);
      });
  }

  /**
   * function for setting competition reference
   * @param competition for setting competition reference
   */
  public set competitionReference(competition: Competition) {
    this.competition = competition;
  }

  /**
   * function for creating bundle of competition data and navigate to add stage window
   */
  onCreateStage(round: Round): void {
    this.stage = null;
    this.round = round;
    this.router.navigateByUrl('/competition/new/add-stage');
  }

  /**
   * function for editing existing stage
   * @param stage for evaluation of specific stage for editing
   */
  onEditStage(round: Round, stage: Stage): void {
    this.stage = stage;
    this.round = round;
    this.router.navigateByUrl('/competition/new/add-stage');
  }

  /**
   * function for removing stage from competition
   * @param stage for evaluation of specific stage
   */
  onDeleteStage(round: Round, stage: Stage): void {
    round.stages.splice(this.round.stages.indexOf(stage), 1);
    this.rounds[this.rounds.indexOf(round)] = round;
  }

  /**
   * function for constructing competition object based
   * if competition is already created as a local storage, use this competition
   * if competition is not yet created as a local storage, construct it from fields
   */
  onReturnCompetition(): Competition {
    if (this.competition === null) {
        return new Competition(null, this.competitionCardForm.get('competitionName').value, CompetitionType.CEC, this.competitionCardForm.get('registrationEnabledFrom').value,
          this.competitionCardForm.get('registrationEnabledTo').value, this.competitionCardForm.get('competitionStart').value, this.competitionCardForm.get('competitionEnd').value, this.rounds, this.onGetAuthorsIds(this.activeAuthors));
    } else {
        this.setCompetitionParametersToFields(this.competition);
        return this.competition;
    }
  }

  /**
   * function for setting competition parameters to UI fields
   * @param competition for obtaining competition parameters
   */
  setCompetitionParametersToFields(competition: Competition): void {
    this.competitionCardForm.setValue({
      competitionName: competition.competitionName,
      registrationEnabledFrom: competition.registrationEnabledFrom,
      registrationEnabledTo: competition.registrationEnabledTo,
      competitionStart: competition.competitionStart,
      competitionEnd: competition.competitionEnd,
      author: null
    });
  }

  /**
   * function for opening dialog for adding new round
   */
  onOpenNewRoundDialog(): void {
    const dialogRef = this.dialog.open(RoundDialogComponent, {data: null});

    dialogRef.afterClosed().subscribe(result => {
      const roundResult = result as Round;
      if (result) { this.rounds.push(roundResult); }
    });
  }

  /**
   * function for getting all competitors to select them as authors
   */
  getAvailableAuthors(): void {
    if (this.activeAuthors.length === 0) {
      this.http.get<CompetitorSimple[]>(SERVER_API_URL + '/api/competitors/simple',
        {headers: this.httpHandlingService.authorizationHeaders()}).subscribe((authors: CompetitorSimple[]) => {
        this.authorsFullData = authors;
        const loggedCompetitor = this.localStorage.get('loggedCompetitor') as CompetitorSimple;
        const author = this.authorsFullData.filter((authorFromResponse: CompetitorSimple) =>
            authorFromResponse.id === loggedCompetitor.id)[0];
        this.activeAuthors.push(author);
        this.authorsFullData.splice(this.authorsFullData.indexOf(author), 1);
      });
    }
  }

  /**
   * function for retrieving authors ids
   * @param authors for evaluation of authors
   */
  onGetAuthorsIds(authors: CompetitorSimple[]): number[] {
    const authorsId = [];
    for (const author of authors) {
      authorsId.push(author.id);
    }
    return authorsId;
  }

  /**
   * function for refiltering authors in dropdown and list
   */
  refilterAuthors(): void {
    this.authorsFullData = this.authorsFullData.filter((author: CompetitorSimple) =>
      this.activeAuthors.filter((activeAuthor: CompetitorSimple) => author.id !== activeAuthor.id));
  }

  /**
   * function for evaluation of available competitors based on additional competitors
   */
  processAuthors(): void {
    const additionalCompetitor = this.competitionCardForm.get('author').value as CompetitorSimple;
    this.activeAuthors.push(additionalCompetitor);
    this.authorsFullData.splice(this.authorsFullData.indexOf(additionalCompetitor), 1);
    this.competitionCardForm.patchValue({author: null});
    this.refilterAuthors();
  }

  /**
   * function for removing desired competitor from additional competitors list
   * @param competitor for evaluation of competitor to delete
   */
  deleteActiveAuthor(competitor: CompetitorSimple): void {
    this.activeAuthors.splice(this.activeAuthors.indexOf(competitor), 1);
    this.refilterAuthors();
  }

  /**
   * remove competition from local storage and return home
   */
  onCancel(): void {
    this.router.navigateByUrl('/home');
  }
}
